import React, { useState, useEffect, useCallback } from "react"
import { Plus, Pencil, Trash2, Tag, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Skeleton } from "src/components/ui/skeleton"
import { useToast } from "src/components/ui/toast"

import { useTags, useDeleteTag } from "src/features/tags/hooks/useTags"
import { TagDrawer } from "src/features/tags/components/TagDrawer"
import { Can } from "src/components/shared/Can"

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export default function TagsPage() {
  const { toast } = useToast()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [searchRaw, setSearch] = useState("")
  const q = useDebounce(searchRaw)

  const { data, loading, fetch } = useTags()
  const deleteHook = useDeleteTag()

  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState(null)

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc")
      else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const reload = useCallback(() => fetch({ q }), [fetch, q])
  useEffect(() => { reload() }, [q])

  const tags = data?.tags ?? []

  const sortedTags = React.useMemo(() => {
    if (!sortField || !sortDirection) return tags
    return [...tags].sort((a, b) => {
      let aVal, bVal
      if (sortField === "name") {
        aVal = a.name || ""
        bVal = b.name || ""
      } else if (sortField === "description") {
        aVal = a.description || ""
        bVal = b.description || ""
      } else if (sortField === "usage") {
        aVal = a.taggings_count || 0
        bVal = b.taggings_count || 0
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      }
    })
  }, [tags, sortField, sortDirection])

  const renderSortableHeader = (label, field, className = "") => {
    const isSorted = sortField === field
    return (
      <Button
        variant="ghost"
        onClick={() => handleSort(field)}
        className={`inline-flex h-auto items-center gap-1 p-0 font-semibold text-black hover:bg-transparent hover:text-black ${className}`}
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
        )}
      </Button>
    )
  }

  function openCreate() { setEditing(null); setDrawerOpen(true) }
  function openEdit(t) { setEditing(t); setDrawerOpen(true) }

  async function handleDelete(t) {
    if (!window.confirm(`Delete tag "${t.name}"? It will be removed from all records.`)) return
    try {
      await deleteHook.mutate(t.id)
      toast({ title: "Tag deleted", description: t.name })
      reload()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : "Could not delete tag"
      toast({ variant: "destructive", title: "Error", description: msg })
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[17px] font-bold text-slate-900">Tags</h2>
          <p className="text-[13px] text-slate-500">Categorize leads, contacts, projects and documents with colored tags.</p>
        </div>
        <Can resource="tags" action="create">
          <Button size="sm" className="gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New tag
          </Button>
        </Can>
      </div>

      <div className="max-w-xs">
        <Input
          placeholder="Search tags…"
          value={searchRaw}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading && !data ? (
        <Skeleton className="h-48 w-full rounded-xl" />
      ) : tags.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-16 text-center">
          <Tag className="h-10 w-10 text-slate-300" />
          <div>
            <p className="font-medium text-slate-700">{q ? "No tags match your search" : "No tags yet"}</p>
            {!q && <p className="text-[13px] text-slate-500">Create your first tag to start categorizing records.</p>}
          </div>
          {!q && (
            <Can resource="tags" action="create">
              <Button size="sm" className="gap-2" onClick={openCreate}>
                <Plus className="h-4 w-4" />
                New tag
              </Button>
            </Can>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[50px]">#</TableHead>
                <TableHead className="text-black">{renderSortableHeader("Tag", "name")}</TableHead>
                <TableHead className="text-black">{renderSortableHeader("Description", "description")}</TableHead>
                <TableHead className="text-right text-black">{renderSortableHeader("Usage", "usage", "w-full justify-end")}</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTags.map((t, idx) => (
                <TableRow key={t.id} className="group">
                  <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <span
                        className="h-4 w-4 shrink-0 rounded-full border border-black/10"
                        style={{ backgroundColor: t.color || "#64748B" }}
                      />
                      <span className="font-medium text-slate-900 text-[13px]">{t.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[320px] truncate text-[13px] text-slate-500">
                    {t.description || <span className="text-slate-300">—</span>}
                  </TableCell>
                  <TableCell className="text-right text-[13px] text-slate-500">
                    {t.taggings_count ?? 0}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-0.5">
                      <Can resource="tags" action="update">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(t)} title="Edit tag">
                          <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                        </Button>
                      </Can>
                      <Can resource="tags" action="destroy">
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(t)} title="Delete tag">
                          <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                        </Button>
                      </Can>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <TagDrawer
        open={drawerOpen}
        tag={editing}
        onClose={() => setDrawerOpen(false)}
        onSuccess={reload}
      />
    </div>
  )
}
